import React from 'react'
import PayrollCalculator from '../components/PayrollCalculator'
import {DollarSign, Shield, TrendingUp} from 'lucide-react'

const HomePage = () => {
  return (
    <div>
      {/* Hero Section with Calculator */}
      <section className="bg-gradient-to-br from-blue-50 to-emerald-50 py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Free Payroll Calculator & Paycheck Estimator
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Instantly calculate your take home pay and net salary after federal tax, state tax, Social Security, and Medicare deductions. Accurate paycheck calculations for all 50 US states.
            </p>
          </div>

          {/* Calculator Component */}
          <PayrollCalculator />
        </div>
      </section>

      {/* SEO Content Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Calculate Salary and Take Home Pay */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Calculate Salary and Take Home Pay
            </h2>
            <p className="text-gray-600 leading-relaxed">
              Our free payroll calculator helps you understand your true earnings by calculating your net pay after all deductions. Whether you're paid weekly, bi-weekly, semi-monthly, monthly, or annually, our calculator provides accurate results based on current federal and state tax rates. Simply enter your gross pay, select your pay frequency, filing status, and state to see a detailed breakdown of your paycheck.
            </p>
          </div>

          {/* How to Calculate My Paycheck */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              How to Calculate My Paycheck
            </h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              Calculating your paycheck involves understanding several key deductions that reduce your gross pay to arrive at your net pay (take home pay). Our calculator automatically computes:
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-blue-50 p-6 rounded-lg">
                <DollarSign className="w-8 h-8 text-blue-800 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Federal Income Tax</h3>
                <p className="text-sm text-gray-600">Based on IRS tax brackets and your filing status</p>
              </div>
              <div className="bg-emerald-50 p-6 rounded-lg">
                <Shield className="w-8 h-8 text-emerald-700 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">State Income Tax</h3>
                <p className="text-sm text-gray-600">Varies by state - accurate rates for all 50 states</p>
              </div>
              <div className="bg-cyan-50 p-6 rounded-lg">
                <TrendingUp className="w-8 h-8 text-cyan-700 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">FICA Taxes</h3>
                <p className="text-sm text-gray-600">Social Security (6.2%) and Medicare (1.45%)</p>
              </div>
            </div>
          </div>

          {/* Accurate Paycheck Tax Calculator */}
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Accurate Paycheck Tax Calculator
            </h2>
            <p className="text-gray-600 leading-relaxed">
              Accuracy matters when planning your finances. Our paycheck tax calculator uses the latest federal tax tables and state tax rates to provide precise calculations. Whether you're comparing job offers, planning a budget, or simply want to understand your paycheck better, our calculator gives you the detailed breakdown you need. The tool accounts for standard deductions, tax brackets, and state-specific rules to ensure you get the most accurate estimate of your take home pay.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-blue-800 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Calculate Your Take Home Pay Now
          </h2>
          <p className="text-blue-100 mb-6">
            Get instant, accurate paycheck calculations. Free to use, no registration required.
          </p>
          <a
            href="#calculator"
            className="inline-block bg-white text-blue-800 font-semibold px-8 py-3 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Use Calculator
          </a>
        </div>
      </section>
    </div>
  )
}

export default HomePage
